import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography, Button, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DocumentCard from '../components/editor/DocumentCard';
import CoverCard from '../components/editor/CoverCard';
import NewDocumentModal from '../components/editor/NewDocumentModal';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../utils/api';
import '../styles/EditorHome.css';

const EditorHome = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openModal, setOpenModal] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [covers, setCovers] = useState([]);
  const [assignedCovers, setAssignedCovers] = useState([]);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user'));
  const userId = user?.id;

  useEffect(() => {
    const fetchAll = async () => {
      const accessToken = localStorage.getItem('rv_access_token');
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
      };

      // Fetch manuscripts and covers in parallel
      try {
        const [manuscriptsRes, coversRes] = await Promise.all([
          fetch(`${API_BASE}/api/drafts`, { credentials: 'omit', headers }),
          fetch(`${API_BASE}/api/drafts?type=cover_only`, { credentials: 'omit', headers }),
        ]);

        // Process manuscripts
        if (manuscriptsRes.ok) {
          const data = await manuscriptsRes.json();
          if (Array.isArray(data)) {
            // Only include drafts with status === 'draft' and not cover_only type
            const filtered = data.filter(d => d.status === 'draft' && d.type !== 'cover_only');
            const enhanced = filtered.map(doc => ({
              id: doc._id,
              title: doc.title || 'Untitled',
              lastEdited: formatLastEdited(doc.lastEdited),
              preview: doc.content?.replace(/<[^>]+>/g, '').slice(0, 100) || '',
              wordCount: countWords(doc.content),
              readingTime: estimateReadTime(doc.content),
            }));
            setDocuments(enhanced);
          }
        } else {
          console.error('Failed to fetch drafts:', manuscriptsRes.status);
          setDocuments([]);
        }

        // Process covers
        if (coversRes.ok) {
          const coverData = await coversRes.json();
          if (Array.isArray(coverData)) {
            const filteredCovers = coverData.filter(d => d.status === 'draft');
            setCovers(filteredCovers.map(doc => ({
              id: doc._id,
              title: doc.title || 'Untitled Cover',
              genre: doc.genre,
              volume: doc.volume,
              coverImage: doc.coverImage,
              cover_url: doc.cover_url,
              coverVersions: doc.coverVersions || [],
              activeCoverVersionId: doc.activeCoverVersionId,
            })));
          }
        } else {
          console.error('Failed to fetch covers:', coversRes.status);
          setCovers([]);
        }
      } catch (err) {
        console.error('Failed to fetch content:', err);
        setDocuments([]);
        setCovers([]);
      }

      // Fetch covers assigned/collaborated on
      try {
        const collabRes = await fetch(`${API_BASE}/api/drafts/collaborator-queue`, { credentials: 'omit', headers });
        if (collabRes.ok) {
          const collabData = await collabRes.json();
          setAssignedCovers(collabData.tasks || []);
        }
      } catch (err) {
        console.error('Failed to fetch collaborator queue:', err);
      }
    };

    fetchAll();
  }, [userId]);

  const formatLastEdited = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  const countWords = (html) => {
    return html ? html.replace(/<[^>]+>/g, '').trim().split(/\s+/).length : 0;
  };

  const estimateReadTime = (html) => {
    const words = countWords(html);
    return Math.max(1, Math.ceil(words / 200));
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      const accessToken = localStorage.getItem('rv_access_token');

      const res = await fetch(`${API_BASE}/api/drafts/${draftId}`, {
        method: 'DELETE',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
        }
      });

      if (!res.ok) {
        console.error('Failed to delete draft:', res.status, res.statusText);
        return;
      }

      setDocuments((prev) => prev.filter((doc) => doc.id !== draftId));
      setCovers((prev) => prev.filter((c) => c.id !== draftId));
    } catch (error) {
      console.error('Failed to delete draft:', error);
    }
  };

  const handleOpenDraft = (draftId) => {
    navigate(`/editor/${draftId}`);
  };

  const handleOpenCover = (coverId) => {
    navigate(`/editor/${coverId}/cover`);
  };

  const handleOpenAssignedCover = (task) => {
    navigate(`/bookcover/${task._id}/designer/${task.versionId || ''}`);
  };

  // Horizontal scroll helpers
  const scrollContainer = (ref, direction) => {
    if (!ref.current) return;
    const amount = direction === 'left' ? -300 : 300;
    ref.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const docsScrollRef = React.useRef(null);
  const coversScrollRef = React.useRef(null);
  const assignedScrollRef = React.useRef(null);

  // Filter both sections by search query
  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredCovers = covers.filter(c =>
    c.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredAssigned = assignedCovers.filter(t =>
    (t.title || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box className="editor-home">
      {!openModal && (
        <>
      {/* Header row: My Content + Add New button */}
      <Box className="editor-home-header">
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          My Content
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
          sx={{
            backgroundColor: '#413B8D',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            borderRadius: 2,
            px: 3,
            py: 1,
            '&:hover': { backgroundColor: '#322E6E' },
          }}
        >
          Add New
        </Button>
      </Box>

      {/* Search bar */}
      <Box className="search-section">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      {/* My Documents section */}
      <Box className="content-section">
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          My Documents
        </Typography>
        <Box className="scroll-wrapper">
          <IconButton
            className="scroll-arrow scroll-arrow-left"
            onClick={() => scrollContainer(docsScrollRef, 'left')}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Box className="scroll-container" ref={docsScrollRef}>
            {filteredDocuments.map((doc) => (
              <DocumentCard
                key={doc.id}
                document={doc}
                onClick={() => handleOpenDraft(doc.id)}
                onDelete={() => handleDeleteDraft(doc.id)}
              />
            ))}
            {filteredDocuments.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                No documents yet. Click "+ Add New" to create one.
              </Typography>
            )}
          </Box>
          <IconButton
            className="scroll-arrow scroll-arrow-right"
            onClick={() => scrollContainer(docsScrollRef, 'right')}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* My Covers section */}
      <Box className="content-section">
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          My Covers
        </Typography>
        <Box className="scroll-wrapper">
          <IconButton
            className="scroll-arrow scroll-arrow-left"
            onClick={() => scrollContainer(coversScrollRef, 'left')}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Box className="scroll-container" ref={coversScrollRef}>
            {filteredCovers.map((cover) => (
              <CoverCard
                key={cover.id}
                cover={cover}
                onClick={() => handleOpenCover(cover.id)}
                onDelete={() => handleDeleteDraft(cover.id)}
              />
            ))}
            {filteredCovers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                No standalone covers yet. Click "+ Add New" and select "Book Cover".
              </Typography>
            )}
          </Box>
          <IconButton
            className="scroll-arrow scroll-arrow-right"
            onClick={() => scrollContainer(coversScrollRef, 'right')}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Assigned Covers section */}
      {(filteredAssigned.length > 0 || assignedCovers.length > 0) && (
        <Box className="content-section">
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Assigned to Me
          </Typography>
          <Box className="scroll-wrapper">
            <IconButton
              className="scroll-arrow scroll-arrow-left"
              onClick={() => scrollContainer(assignedScrollRef, 'left')}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Box className="scroll-container" ref={assignedScrollRef}>
              {filteredAssigned.map((task) => (
                <CoverCard
                  key={`${task._id}-${task.versionId || ''}`}
                  cover={{
                    id: task._id,
                    title: task.title,
                    genre: task.genre,
                    volume: task.volume,
                    cover_url: task.cover_url,
                    coverVersions: task.versionId
                      ? [{ id: task.versionId, coverStatus: task.coverStatus, coverType: null }]
                      : [],
                    activeCoverVersionId: task.versionId,
                  }}
                  onClick={() => handleOpenAssignedCover(task)}
                  onDelete={null}
                  assignedRole={task.collaboratorRole}
                  versionName={task.versionName}
                />
              ))}
            </Box>
            <IconButton
              className="scroll-arrow scroll-arrow-right"
              onClick={() => scrollContainer(assignedScrollRef, 'right')}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      )}

        </>
      )}

      <NewDocumentModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          navigate('/dashboard');
        }}
      />
    </Box>
  );
};

export default EditorHome;
